import type { CellContext } from '@tanstack/react-table';
import React from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@daater/api';
import { useNavigate } from '@tanstack/react-router';
import {
  Bell,
  ChartBarBigIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileSpreadsheet,
  LogOut,
  Menu,
  Package,
  X,
} from 'lucide-react';
import {
  queryKeys,
  useDeleteSavedReportMutation,
  useDataStructureQuery,
  useSavedReportsQuery,
  useSwitchAccountMutation,
  useTableFieldsQuery,
  useTableFiltersQuery,
  useTableRecordsQuery,
  useUpdateSettingsMutation,
} from '../api/queries';
import type { TableColumnSettings } from '../api/types';
import type {
  ColumnMapping,
  PaginatedResponse,
  PendingSavedSearchPayload,
  RecordsFilter,
  SavedReportItem,
  SearchRecordsBody,
  TableField,
  TableFilter,
} from '../api/types';
import { logoutRequest, type DataStructureDataset } from '../api/endpoints';
import { useAccountStore } from '../lib/account-store';
import { formatFilterOptionLabel } from '../lib/filter-labels';
import { useAuthStore } from '../lib/auth-store';
import {
  createSavedReport,
  requestLegacyExport,
  fetchReportStatus,
  fetchFilterAutocomplete,
  exportTableRecords,
  trackExportEvent,
} from '../api/endpoints';
import { useSessionState, clearDashboardSession } from '../hooks/useSessionState';
import SearchCards from '../components/search_cards';
import AnalysisSection from '../components/analysis-section';
import DataSection from '../components/data-section';
import DaaterLogoHomeLink from '../components/daater-logo-home-link';
import SavedSearchesSection from '../components/saved-searches-section';
import { useOpenSavedSearch } from '../hooks/useOpenSavedSearch';
import { useClickOutside } from '../hooks/useClickOutside';
import { useNotificationStore } from '../lib/notification-store';
import NotificationDropdown from '../components/notification-dropdown';
import {
  clearPendingSavedSearchPayload,
  loadPendingSavedSearchPayload,
  PENDING_SAVED_SEARCH_EVENT,
} from '../lib/saved-searches';
import {
  createDefaultFilterValueState,
  type FilterValueState,
  type DateRange,
  type TeamDetailRestrictions,
  hydrateFiltersFromHistoric,
  hydrateFiltersFromSavedReport,
  validateDateRange,
  buildFiltersBody,
  getCountryFlag,
  parseDateRangeFromTableName,
  createEmptyFilterValues,
  applyDateRangeToFilters,
  generateYearQuickSelects,
  fieldIdsToVisibilityState,
  getDisplayColumnHeader,
  getMappingColumnName,
  columnsToVisibilityState,
  canAccessDetailedMode,
  shouldUseDemoMode,
  separateFiltersByDetailMode,
  limitDemoRecords,
  extractApiErrorMessage,
  extractReportFilePath,
  isReportGenerationInProgressError,
  getSearchColumns,
  visibleFieldsForMode,
  hasUnloadedColumns,
} from './table-data-detail.utils';
import { getAnalysisColumnNames } from '../components/advanced-charts/utils';
import type { VisibilityState } from '@tanstack/react-table';
import { DetailModeSwitch } from '../components/detail-mode-switch';
import { FilterFieldRenderer } from './table-data-detail/filters/FilterFieldRenderer';
import type { MappedFilter } from './table-data-detail/filters/types';

const isDateValue = (value: unknown) => {
  if (typeof value !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}/.test(value);
};

const renderCellValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '-';
};

const buildColumns = (
  mapping: ColumnMapping[] | undefined,
  sampleRow?: Record<string, unknown>
): ColumnDef<Record<string, unknown>>[] => {
  if (!mapping?.length) {
    if (!sampleRow) return [];
    return Object.keys(sampleRow).map(key => ({
      accessorKey: key,
      header: key,
      cell: (info: CellContext<Record<string, unknown>, unknown>) =>
        renderCellValue(info.getValue()),
      filterFn:
        typeof sampleRow[key] === 'number'
          ? 'numberRange'
          : isDateValue(sampleRow[key])
            ? 'dateRange'
            : 'includesString',
    }));
  }

  return mapping
    .map(column => {
      const accessorKey = column.optionalName || column.internalName || column.name || column._id;
      if (!accessorKey) return null;

      const sampleValue = sampleRow ? sampleRow[accessorKey] : undefined;
      const filterFnValue =
        typeof sampleValue === 'number'
          ? 'numberRange'
          : isDateValue(sampleValue)
            ? 'dateRange'
            : 'includesString';

      return {
        accessorKey,
        header: getDisplayColumnHeader(column, mapping, sampleRow),
        cell: (info: CellContext<Record<string, unknown>, unknown>) =>
          renderCellValue(info.getValue()),
        filterFn: filterFnValue,
      };
    })
    .filter(Boolean) as ColumnDef<Record<string, unknown>>[];
};

const inputClassName =
  'block w-full rounded-lg border border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-20 text-sm py-3 px-3 transition-shadow';

const sidebarTabClassName =
  'flex items-center gap-3 rounded-lg px-4 py-3 text-[13px] transition-all duration-200';

const HISTORIC_STORAGE_KEY = 'historicSearch';

const LOOKUP_FILTER_OPTIONS_LIMIT = 120;
const EMPTY_VISIBILITY: VisibilityState = {};
const EMPTY_COLUMNS: string[] = [];
// Espera antes de volver a buscar al activar columnas, para agrupar varios clics seguidos.
const COLUMN_RELOAD_DELAY_MS = 400;

const formatReportTimestamp = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export default function TableDataDetailScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useAuthStore(state => state.token);
  const user = useAuthStore(state => state.user);
  const clearAuth = useAuthStore(state => state.clearAuth);
  const account = useAccountStore(state => state.account);
  const clearAccount = useAccountStore(state => state.clearAccount);
  const [selectedSection, setSelectedSection] = useSessionState<string | undefined>(
    'selectedSection',
    undefined
  );
  const [selectedDataset, setSelectedDataset] = useSessionState<string | undefined>(
    'selectedDataset',
    account?.dataset
  );
  const [appliedTableId, setAppliedTableId] = useSessionState<string | undefined>(
    'appliedTableId',
    account?.table
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useSessionState('sidebarCollapsed', false);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const [notifOpen, setNotifOpen] = React.useState(false);
  const notifRef = React.useRef<HTMLDivElement | null>(null);
  const unreadCount = useNotificationStore(s => s.unreadCount);
  const popupRef = React.useRef<HTMLDivElement | null>(null);
  const switchAccountMutation = useSwitchAccountMutation();
  const updateSettingsMutation = useUpdateSettingsMutation();
  const sidebarTheme = (import.meta.env.VITE_SIDEBAR_THEME ?? '').toLowerCase().trim();
  const isWhiteBlueSidebar =
    sidebarTheme === 'blanco-azul' ||
    sidebarTheme === 'blanco-auzl' ||
    sidebarTheme === 'white-blue';
  const sidebarClasses = {
    container: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-900 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)]'
      : 'border-white/10 bg-gradient-to-b from-[#0d2147]/90 via-[#0b1c3d]/90 to-[#08152f]/90 text-white shadow-[0_20px_80px_-30px_rgba(6,12,28,0.9)] backdrop-blur-2xl',
    logo: isWhiteBlueSidebar
      ? ''
      : 'brightness-0 invert drop-shadow-[0_6px_16px_rgba(255,255,255,0.15)]',
    iconButton: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-900'
      : 'border-white/20 bg-white/5 text-white/80 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.45)] hover:bg-white/10 hover:text-white',
    sectionLabel: isWhiteBlueSidebar ? 'text-slate-500' : 'text-white/50',
    selectFlag: isWhiteBlueSidebar
      ? 'bg-slate-100 text-slate-700 shadow-[0_8px_20px_-16px_rgba(15,23,42,0.25)]'
      : 'bg-white/10 text-white shadow-[0_8px_20px_-16px_rgba(0,0,0,0.7)]',
    select: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-900 shadow-[0_10px_24px_-16px_rgba(15,23,42,0.2)] focus:border-primary focus:ring focus:ring-primary/20'
      : 'border-white/15 bg-white/10 text-white shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)] backdrop-blur-md focus:border-primary focus:ring focus:ring-primary/20',
    selectCaret: isWhiteBlueSidebar ? 'text-slate-400' : 'text-blue-200',
    baseDivider: isWhiteBlueSidebar ? 'border-slate-200' : 'border-white/10',
    baseActive: isWhiteBlueSidebar
      ? 'bg-primary text-white shadow-[0_12px_24px_-16px_rgba(37,99,235,0.5)] font-semibold border border-primary/30 ring-1 ring-primary/20'
      : 'bg-gradient-to-r from-white/30 via-white/15 to-white/5 text-white shadow-[0_16px_30px_-20px_rgba(0,0,0,0.7)] font-semibold border border-white/40 ring-1 ring-white/20',
    baseInactive: isWhiteBlueSidebar
      ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 font-normal border border-transparent'
      : 'text-blue-100 hover:bg-white/10 hover:text-white font-normal border border-transparent',
    baseIcon: isWhiteBlueSidebar
      ? 'h-6 w-6 inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center p-0.5 text-primary text-sm rounded-full bg-primary/10 border-primary/30 shadow-[inset_0_1px_0px_rgba(255,255,255,0.9),0_0_8px_rgba(37,99,235,0.25),0_3px_8px_rgba(15,23,42,0.12)] hover:bg-primary/15 transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-white/70 before:via-transparent before:to-transparent before:opacity-70 before:pointer-events-none after:absolute after:inset-0 after:rounded-full after:bg-gradient-to-tl after:from-primary/20 after:via-transparent after:to-transparent after:opacity-60 after:pointer-events-none antialiased'
      : 'h-6 w-6 inline-flex items-center justify-center border align-middle select-none font-sans font-medium text-center p-0.5 text-white text-sm font-medium rounded-full bg-white/2.5 border border-white/50 backdrop-blur-sm shadow-[inset_0_1px_0px_rgba(255,255,255,0.75),0_0_9px_rgba(0,0,0,0.2),0_3px_8px_rgba(0,0,0,0.15)] hover:bg-white/30 transition-all duration-300 before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-white/60 before:via-transparent before:to-transparent before:opacity-70 before:pointer-events-none after:absolute after:inset-0 after:rounded-full after:bg-gradient-to-tl after:from-white/30 after:via-transparent after:to-transparent after:opacity-50 after:pointer-events-none transition antialiased',
    emptyText: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-100',
    collapsedLabel: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-200',
    collapsedFlag: isWhiteBlueSidebar
      ? 'border-slate-200 bg-slate-50 text-slate-700'
      : 'border-white/20 bg-white/10 text-white',
    collapsedBaseActive: isWhiteBlueSidebar
      ? 'border-primary bg-primary text-white'
      : 'border-white/50 bg-gradient-to-br from-white/35 via-white/20 to-white/5 text-white',
    collapsedBaseInactive: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900'
      : 'border-white/10 bg-white/5 text-blue-100 hover:bg-white/10 hover:text-white',
    menuButton: isWhiteBlueSidebar
      ? 'border-slate-200 bg-white text-slate-800 shadow-[0_16px_36px_-24px_rgba(15,23,42,0.25)] hover:bg-slate-50'
      : 'border-white/15 bg-white/10 text-white shadow-[0_16px_36px_-24px_rgba(0,0,0,0.7)] backdrop-blur-md hover:bg-white/15',
    menuSubText: isWhiteBlueSidebar ? 'text-slate-500' : 'text-blue-200',
    menuChevron: isWhiteBlueSidebar ? 'text-slate-400' : 'text-blue-200',
  };

  const { data: dataStructure, isLoading: dataStructureLoading } = useDataStructureQuery(
    Boolean(token)
  );
  const datasets = React.useMemo(() => dataStructure?.datasets ?? [], [dataStructure]);

  const sectionData = React.useMemo(() => {
    const bySection = new Map<string, DataStructureDataset[]>();
    const codeBySection = new Map<string, string | null>();
    datasets.forEach(dataset => {
      const section =
        typeof dataset.section === 'string' && dataset.section.trim() ? dataset.section : 'General';
      const items = bySection.get(section) ?? [];
      items.push(dataset);
      bySection.set(section, items);
      if (!codeBySection.has(section)) {
        codeBySection.set(section, dataset.countryCode ?? null);
      }
    });
    return { list: Array.from(bySection.keys()), bySection, codeBySection };
  }, [datasets]);
  const datasetsForSection = React.useMemo(() => {
    if (selectedSection) {
      return sectionData.bySection.get(selectedSection) ?? [];
    }
    return datasets;
  }, [selectedSection, sectionData.bySection, datasets]);
  const datasetId = selectedDataset ?? datasetsForSection[0]?._id;
  const tables = React.useMemo(
    () => (dataStructure?.tables ?? []).filter(t => t.datasetId === datasetId),
    [dataStructure, datasetId]
  );
  const tablesLoading = dataStructureLoading;
  const sidebarDatasets = React.useMemo(() => dataStructure?.datasets ?? [], [dataStructure]);
  const sidebarDatasetIds = React.useMemo(
    () => new Set(sidebarDatasets.map(d => d._id)),
    [sidebarDatasets]
  );
  const sidebarTables = React.useMemo(() => {
    const activeSectionCode = selectedSection
      ? (sectionData.codeBySection.get(selectedSection) ?? null)
      : null;
    return (dataStructure?.tables ?? []).filter(
      t =>
        !sidebarDatasetIds.has(t.datasetId) &&
        (!activeSectionCode || t.countryCode === activeSectionCode)
    );
  }, [dataStructure?.tables, sidebarDatasetIds, selectedSection, sectionData.codeBySection]);
  const activeTableIsFromSidebar = React.useMemo(
    () => sidebarTables.some(t => t._id === appliedTableId),
    [sidebarTables, appliedTableId]
  );
  const { data: savedReportsData, isLoading: savedReportsLoading } =
    useSavedReportsQuery(appliedTableId);
  const savedReports = React.useMemo(() => savedReportsData?.data ?? [], [savedReportsData]);
  const activeTableId = appliedTableId;
  const openSavedSearch = useOpenSavedSearch();
  const deleteSavedReportMutation = useDeleteSavedReportMutation(appliedTableId);
  const userData = account?.userData as Record<string, unknown> | undefined;
  const userFirstName = typeof userData?.firstName === 'string' ? userData.firstName : '';
  const userLastName = typeof userData?.lastName === 'string' ? userData.lastName : '';
  const userFullName = `${userFirstName} ${userLastName}`.trim();
  const resolvedEmail =
    user?.email ?? (typeof userData?.email === 'string' ? userData.email : '') ?? '';
  const displayName =
    userFullName ||
    (typeof user?.name === 'string' && user.name.trim() ? user.name.trim() : '') ||
    resolvedEmail ||
    'Usuario';
  const initials = displayName.slice(0, 2).toUpperCase();
  const accountPlan: unknown =
    (account as Record<string, unknown>)?.plan ??
    (typeof (account as Record<string, unknown>)?.teamData === 'object' &&
    (account as Record<string, unknown>)?.teamData !== null
      ? ((account as Record<string, unknown>)?.teamData as Record<string, unknown>)?.plan
      : undefined);
  const accountDaysLeft: unknown =
    (account as Record<string, unknown>)?.daysLeft ??
    (typeof (account as Record<string, unknown>)?.teamData === 'object' &&
    (account as Record<string, unknown>)?.teamData !== null
      ? ((account as Record<string, unknown>)?.teamData as Record<string, unknown>)?.daysLeft
      : undefined);
  const accountEmail = resolvedEmail || 'Usuario';

  // Detailed mode state
  const [detailActived, setDetailActived] = useSessionState('detailActived', false);
  const [demoMode, setDemoMode] = useSessionState('demoMode', false);
  const [appliedFilters, setAppliedFilters] = React.useState<RecordsFilter[]>([]);
  const { data: allFields = [], isLoading: fieldsLoading } = useTableFieldsQuery(activeTableId);
  const isActiveTableDetail = React.useMemo(
    () => tables.find(table => table._id === activeTableId)?.isDetailTable ?? false,
    [tables, activeTableId]
  );
  // En una tabla de detalle, las columnas marcadas como de detalle solo existen
  // dentro de la vista de detalle. Se recorta aquí, en el origen, para que la
  // grilla, las columnas pedidas a la búsqueda y la selección por defecto salgan
  // todas del mismo conjunto.
  const fields = React.useMemo(
    () => visibleFieldsForMode(allFields, isActiveTableDetail, detailActived),
    [allFields, isActiveTableDetail, detailActived]
  );
  const { data: filtersData, isLoading: filtersLoading } = useTableFiltersQuery(activeTableId);
  const [activeColumnVisibility, setActiveColumnVisibility] = React.useState<VisibilityState>({});
  const visibleTableColumns = React.useMemo(
    () => getSearchColumns(fields, activeColumnVisibility),
    [fields, activeColumnVisibility]
  );
  // Columnas que pide la búsqueda: las visibles en la tabla más las que lee la sección de análisis
  // (métricas, fechas, dimensiones), para que ocultar una columna no cambie los gráficos.
  const requestedColumns = React.useMemo(() => {
    if (!visibleTableColumns.length) return EMPTY_COLUMNS;
    const analysisColumns = getAnalysisColumnNames(fields, filtersData ?? []);
    return Array.from(new Set([...visibleTableColumns, ...analysisColumns])).sort();
  }, [visibleTableColumns, fields, filtersData]);
  // Columnas fijadas a la búsqueda aplicada: ocultar columnas no vuelve a buscar.
  const [pinnedColumns, setPinnedColumns] = React.useState<{
    tableId: string | undefined;
    filters: RecordsFilter[];
    columns: string[];
  } | null>(null);
  const searchColumns =
    pinnedColumns &&
    pinnedColumns.tableId === activeTableId &&
    pinnedColumns.filters === appliedFilters
      ? pinnedColumns.columns
      : requestedColumns;
  React.useEffect(() => {
    const pinRequestedColumns = () =>
      setPinnedColumns({
        tableId: activeTableId,
        filters: appliedFilters,
        columns: requestedColumns,
      });
    if (
      !pinnedColumns ||
      pinnedColumns.tableId !== activeTableId ||
      pinnedColumns.filters !== appliedFilters
    ) {
      pinRequestedColumns();
      return;
    }
    // Activar una columna que no vino en la respuesta sí vuelve a buscar (agrupando clics seguidos).
    if (!hasUnloadedColumns(requestedColumns, pinnedColumns.columns)) return;
    const timer = window.setTimeout(pinRequestedColumns, COLUMN_RELOAD_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [activeTableId, appliedFilters, requestedColumns, pinnedColumns]);
  const recordsBody = React.useMemo<SearchRecordsBody>(
    () => ({
      page: 1,
      limit: 1000,
      filters: appliedFilters,
      detailed: detailActived,
      demoMode,
      columns: searchColumns,
    }),
    [appliedFilters, detailActived, demoMode, searchColumns]
  );
  const {
    data: recordsData,
    isLoading: recordsLoading,
    isFetching: recordsFetching,
    error: recordsError,
  } = useTableRecordsQuery(activeTableId, recordsBody);
  const [filterValues, setFilterValues] = React.useState<Record<string, FilterValueState>>({});
  const [moreFiltersActive, setMoreFiltersActive] = useSessionState('moreFiltersActive', false);
  const [pendingHistoricSearch, setPendingHistoricSearch] = React.useState<Record<
    string,
    unknown
  > | null>(null);
  const [pendingSavedSearch, setPendingSavedSearch] =
    React.useState<PendingSavedSearchPayload | null>(null);
  const [historicApplied, setHistoricApplied] = React.useState(false);
  const [savedSearchApplied, setSavedSearchApplied] = React.useState(false);
  const [searchRequested, setSearchRequested] = React.useState(false);
  const [hasSearchRun, setHasSearchRun] = React.useState(false);
  const [searchError, setSearchError] = React.useState<string | null>(null);
  const [isExportingReport, setIsExportingReport] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState<string | null>(null);
  const [isSavingReport, setIsSavingReport] = React.useState(false);
  const [savedReport, setSavedReport] = React.useState(false);
  const [saveFeedback, setSaveFeedback] = React.useState<string | null>(null);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [openingSavedReportId, setOpeningSavedReportId] = React.useState<string | null>(null);
  const [deletingSavedReportId, setDeletingSavedReportId] = React.useState<string | null>(null);
  const exportTimerRef = React.useRef<number | null>(null);
  const exportFileRef = React.useRef<string | null>(null);
  const settingsSaveTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Guard: only sync account store → local state once (initial hydration).
  // Subsequent account store changes (e.g. from refetchOnWindowFocus picking up
  // another tab's switchAccount call) must NOT overwrite local selections.
  // Also skip if session already restored selections (tab discard recovery).
  const hasInitialSyncRef = React.useRef(
    Boolean(account?.dataset) ||
      Boolean(account?.table) ||
      Boolean(selectedDataset) ||
      Boolean(appliedTableId)
  );

  React.useEffect(() => {
    if (hasInitialSyncRef.current) return;
    if (account?.dataset || account?.table) {
      hasInitialSyncRef.current = true;
      if (account?.dataset) setSelectedDataset(account.dataset);
      if (account?.table) setAppliedTableId(account.table);
    }
  }, [account?.dataset, account?.table, setAppliedTableId, setSelectedDataset]);

  React.useEffect(() => {
    // Hide header on desktop, show on mobile
    document.body.classList.add('hide-header-desktop');
    return () => {
      document.body.classList.remove('hide-header-desktop');
    };
  }, []);

  React.useEffect(() => {
    return () => {
      if (exportTimerRef.current !== null) {
        window.clearInterval(exportTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    if (!selectedDataset && datasetId) setSelectedDataset(datasetId);
  }, [datasetId, selectedDataset, setSelectedDataset]);

  React.useEffect(() => {
    if (selectedSection || !sectionData.list.length) return;
    const selected = datasets.find(
      dataset => dataset._id === selectedDataset || dataset._id === account?.dataset
    );
    const section =
      selected && typeof selected.section === 'string' && selected.section.trim()
        ? selected.section
        : sectionData.list[0];
    if (section) {
      setSelectedSection(section);
    }
  }, [
    selectedSection,
    sectionData.list,
    datasets,
    selectedDataset,
    account?.dataset,
    setSelectedSection,
  ]);

  React.useEffect(() => {
    if (!selectedSection) return;
    if (!datasetsForSection.length) return;
    if (selectedDataset && datasetsForSection.some(dataset => dataset._id === selectedDataset)) {
      return;
    }
    setSelectedDataset(datasetsForSection[0]?._id);
  }, [selectedSection, datasetsForSection, selectedDataset, setSelectedDataset]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(notifRef, () => setNotifOpen(false));

  const [popupPosition, setPopupPosition] = React.useState({ bottom: 0, left: 0 });
  React.useLayoutEffect(() => {
    if (!menuOpen || !menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    setPopupPosition({
      bottom: window.innerHeight - rect.top + 8,
      left: rect.left,
    });
  }, [menuOpen]);

  React.useEffect(() => {
    setAppliedFilters([]);
    setFilterValues({});
    setSearchRequested(false);
    setHasSearchRun(false);
    setSearchError(null);
    setSaveFeedback(null);
    setSaveError(null);
    // Reset detailed mode when switching tables
    setDetailActived(false);
    setDemoMode(false);
    setSavedReport(false);
    setSavedSearchApplied(false);
  }, [activeTableId, setDemoMode, setDetailActived]);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const raw = window.localStorage.getItem(HISTORIC_STORAGE_KEY);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      setPendingHistoricSearch(parsed);
    } catch {
      window.localStorage.removeItem(HISTORIC_STORAGE_KEY);
    }
  }, []);

  React.useEffect(() => {
    const loadPendingSavedSearch = () => {
      const payload = loadPendingSavedSearchPayload();
      if (!payload) return;
      setSavedSearchApplied(false);
      setPendingSavedSearch(payload);
    };

    loadPendingSavedSearch();
    window.addEventListener(PENDING_SAVED_SEARCH_EVENT, loadPendingSavedSearch);
    return () => window.removeEventListener(PENDING_SAVED_SEARCH_EVENT, loadPendingSavedSearch);
  }, []);

  const handleTableApply = async (nextTableId: string, datasetIdOverride?: string) => {
    const effectiveDatasetId = datasetIdOverride ?? datasetId;
    if (!account?._id || !effectiveDatasetId) return;

    setAppliedTableId(nextTableId);

    await switchAccountMutation.mutateAsync({
      account: account._id,
      dataset: effectiveDatasetId,
      table: nextTableId,
    });

    void navigate({ to: `/data/table/${nextTableId ?? ''}` });
  };

  const handleClearFilters = () => {
    const emptyValues = createEmptyFilterValues(mappedFilters);

    // Re-apply date range from current table if available
    const currentTable = tables.find(t => t._id === appliedTableId);
    if (currentTable?.name) {
      const dateRange = parseDateRangeFromTableName(currentTable.name);
      if (dateRange) {
        const valuesWithDateRange = applyDateRangeToFilters(mappedFilters, emptyValues, dateRange);
        setFilterValues(valuesWithDateRange);
        return;
      }
    }

    setFilterValues(emptyValues);
    setSearchError(null);
  };

  const tableRecords = React.useMemo<Array<Record<string, unknown>>>(
    () => recordsData?.data ?? [],
    [recordsData]
  );
  // Lo decide el SERVIDOR, no el estado local: el API ya devuelve recortado y
  // marca la respuesta. Mantener el recorte aquí es defensa en profundidad, no
  // el control — cuando lo era, las filas completas ya habían viajado.
  const isDemoResponse = recordsData?.demo === true;
  const displayRecords = React.useMemo(
    () => limitDemoRecords(tableRecords, isDemoResponse),
    [tableRecords, isDemoResponse]
  );

  // Partidas que el API filtró por estar fuera del plan del usuario.
  const deniedPartidas = recordsData?.deniedPartidas ?? [];
  const mapping = React.useMemo<ColumnMapping[]>(
    () =>
      [...fields]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .flatMap(field => {
          const base: ColumnMapping = {
            _id: field.id,
            internalName: field.internalName,
            publicName: field.publicName,
            name: field.publicName,
          };
          if (field.showKey) {
            return [
              base,
              {
                _id: field.id + '__value',
                internalName: field.internalName + '__value',
                publicName: field.publicName,
                name: field.publicName,
              } as ColumnMapping,
            ];
          }
          return [base];
        }),
    [fields]
  );
  const columns = React.useMemo(
    () => buildColumns(mapping, tableRecords[0]),
    [mapping, tableRecords]
  );

  const filters = React.useMemo(() => filtersData ?? [], [filtersData]);
  // Sobre el conjunto SIN recortar, igual que `fieldsById` en el API: el recorte
  // de columnas de detalle es de proyección, no de permisos, y un filtro cuyo
  // campo sea de detalle debe seguir resolviendo su `fieldData` en la vista
  // agregada en vez de quedarse sin él.
  const fieldsById = new Map(allFields.map(field => [field.id, field]));
  const mappedFilters = filters
    .map(filter => ({
      ...filter,
      fieldData: fieldsById.get(filter.fieldId),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const currentDataset = datasets.find(dataset => dataset._id === datasetId);
  const currentSection =
    selectedSection ??
    (typeof currentDataset?.section === 'string' ? currentDataset?.section : undefined);
  const currentSectionCode = sectionData.codeBySection.get(currentSection ?? '') ?? null;
  // Separate filters by detail mode
  const { standardFilters, detailFilters } = React.useMemo(() => {
    return separateFiltersByDetailMode(mappedFilters);
  }, [mappedFilters]);

  // Get team restrictions for detailed mode
  const teamRestrictions = React.useMemo((): TeamDetailRestrictions | undefined => {
    const teamData = (account as Record<string, unknown>)?.teamData;
    if (teamData && typeof teamData === 'object') {
      return (teamData as Record<string, unknown>).detail as TeamDetailRestrictions | undefined;
    }
    return undefined;
  }, [account]);

  // Check if user can access detailed mode (respects backend isDetailTable flag)
  const canUseDetailMode = React.useMemo(() => {
    const currentTable = tables.find(table => table._id === activeTableId);
    return canAccessDetailedMode(
      account as Record<string, unknown>,
      teamRestrictions,
      currentTable
    );
  }, [account, teamRestrictions, tables, activeTableId]);

  // Check if user should use demo mode when detail is active
  const shouldUseDemo = React.useMemo(() => {
    return shouldUseDemoMode(account as Record<string, unknown>);
  }, [account]);

  // Toggle detailed mode handler
  const handleToggleDetailMode = React.useCallback(() => {
    setDetailActived(prev => {
      const newValue = !prev;
      // If turning off detail mode, also turn off demo mode
      if (!newValue) {
        setDemoMode(false);
      } else if (shouldUseDemo) {
        // If turning on and user should use demo, enable demo mode
        setDemoMode(true);
      }
      return newValue;
    });
  }, [setDemoMode, setDetailActived, shouldUseDemo]);

  const filtersToShow = React.useMemo(() => {
    // Combine standard filters with detail filters when detail mode is active
    const baseFilters = detailActived ? [...standardFilters, ...detailFilters] : standardFilters;

    const visibleFilters = baseFilters.filter(filter => !filter.isCalculating);
    const featured = visibleFilters.filter(filter => filter.featured);
    const nonFeatured = visibleFilters.filter(filter => !filter.featured);
    const ordered = [...featured, ...nonFeatured];
    if (moreFiltersActive) return ordered;
    return ordered.slice(0, 6);
  }, [standardFilters, detailFilters, detailActived, moreFiltersActive]);

  const tableDateRange = React.useMemo((): DateRange | null => {
    const currentTable = tables.find(t => t._id === appliedTableId);
    if (!currentTable?.name) return null;
    return parseDateRangeFromTableName(currentTable.name);
  }, [tables, appliedTableId]);

  const yearQuickSelects = React.useMemo(() => {
    return generateYearQuickSelects(tableDateRange);
  }, [tableDateRange]);

  // Get user's saved column settings from account data
  const userTableSettings = React.useMemo((): TableColumnSettings[] => {
    const settings = (account as Record<string, unknown>)?.settings;
    if (
      settings &&
      typeof settings === 'object' &&
      'tables' in settings &&
      Array.isArray((settings as Record<string, unknown>).tables)
    ) {
      return (settings as Record<string, unknown>).tables as TableColumnSettings[];
    }
    return [];
  }, [account]);

  // Find saved column settings for current table
  const savedColumnSettings = React.useMemo(() => {
    if (!appliedTableId) return null;
    return userTableSettings.find(setting => setting.id === appliedTableId) ?? null;
  }, [userTableSettings, appliedTableId]);

  // Get all column names from the mapping
  const allColumnNames = React.useMemo(() => {
    return mapping.map(getMappingColumnName).filter(Boolean);
  }, [mapping]);

  // Compute initial column visibility based on saved settings or field defaults
  const initialColumnVisibility = React.useMemo((): VisibilityState => {
    if (!allColumnNames.length) return EMPTY_VISIBILITY;

    // If user has saved settings for this table, use them
    if (savedColumnSettings?.columns?.length) {
      const savedSet = new Set(savedColumnSettings.columns);
      const computed = columnsToVisibilityState(allColumnNames, savedColumnSettings.columns);
      // __value companion columns are new virtual columns — default to visible if not explicitly saved
      allColumnNames.forEach(col => {
        if (col.endsWith('__value') && !savedSet.has(col)) {
          computed[col] = true;
        }
      });
      // Guard: if saved columns are stale and none match current columns, show all
      const hasAnyVisible = Object.values(computed).some(v => v);
      if (!hasAnyVisible) return EMPTY_VISIBILITY;
      return computed;
    }

    // Default: all columns visible
    return EMPTY_VISIBILITY;
  }, [allColumnNames, savedColumnSettings]);

  React.useEffect(() => {
    setActiveColumnVisibility(initialColumnVisibility);
  }, [initialColumnVisibility, activeTableId]);

  // Handle column visibility changes - save to backend
  const handleColumnVisibilityChange = React.useCallback(
    (state: VisibilityState) => {
      if (!appliedTableId) return;
      // Update immediately so export uses the current state
      setActiveColumnVisibility(state);
      setSavedReport(false);

      // Debounce only the settings mutation to avoid hammering the API on rapid toggles
      if (settingsSaveTimerRef.current) {
        clearTimeout(settingsSaveTimerRef.current);
      }
      settingsSaveTimerRef.current = setTimeout(() => {
        const visibleColumns = allColumnNames.filter(col => state[col] !== false);
        updateSettingsMutation.mutate({ tableId: appliedTableId, columns: visibleColumns });
      }, 500);
    },
    [appliedTableId, allColumnNames, updateSettingsMutation]
  );

  const handleYearQuickSelect = React.useCallback(
    (range: DateRange, filterId: string) => {
      setFilterValues(prev => ({
        ...prev,
        [filterId]: {
          ...(prev[filterId] ?? createDefaultFilterValueState()),
          min: range.minDate,
          max: range.maxDate,
          touched: true,
        },
      }));
    },
    [setFilterValues]
  );

  React.useEffect(() => {
    if (!mappedFilters.length) return;
    setFilterValues(prev => {
      const next = { ...prev };
      let createdState = false;
      mappedFilters.forEach(filter => {
        if (!next[filter.id]) {
          next[filter.id] = createDefaultFilterValueState();
          createdState = true;
        }
      });

      if (!createdState) {
        return prev;
      }

      // Apply date range from current table name to date filters
      const currentTable = tables.find(t => t._id === appliedTableId);
      if (currentTable?.name) {
        const dateRange = parseDateRangeFromTableName(currentTable.name);
        if (dateRange) {
          return applyDateRangeToFilters(mappedFilters, next, dateRange);
        }
      }

      return next;
    });
  }, [mappedFilters, appliedTableId, tables]);

  const fetchFilterOptions = React.useCallback(
    // Quién puede ofrecer opciones lo decide `filterOffersOptionList`, y lo decide una sola
    // vez, en el campo que las pide. Tener aquí una segunda condición fue el fallo: el campo
    // agendaba la consulta con una regla y esta la descartaba con otra, así que los filtros de
    // maestra sin `autocomplete` no llegaban a emitir ni una petición ni un error.
    async (filter: MappedFilter, query: string) => {
      if (!appliedTableId) return;
      setFilterValues(prev => ({
        ...prev,
        [filter.id]: {
          ...(prev[filter.id] ?? createDefaultFilterValueState()),
          isFetching: true,
        },
      }));
      try {
        const data = await fetchFilterAutocomplete(
          appliedTableId,
          filter.id,
          query,
          LOOKUP_FILTER_OPTIONS_LIMIT
        );
        const items = data.map(({ value, name }) => ({
          value,
          label: name,
          displayLabel: formatFilterOptionLabel(name, value),
        }));
        setFilterValues(prev => ({
          ...prev,
          [filter.id]: {
            ...(prev[filter.id] ?? createDefaultFilterValueState()),
            items,
            isFetching: false,
          },
        }));
      } catch {
        setFilterValues(prev => ({
          ...prev,
          [filter.id]: {
            ...(prev[filter.id] ?? createDefaultFilterValueState()),
            isFetching: false,
          },
        }));
      }
    },
    [appliedTableId]
  );

  const updateFilterState = React.useCallback(
    (filterId: string, updater: (current: FilterValueState) => FilterValueState) => {
      setFilterValues(prev => {
        const current = prev[filterId] ?? createDefaultFilterValueState();
        const nextState = updater(current);
        return {
          ...prev,
          [filterId]: nextState,
        };
      });
    },
    [setFilterValues]
  );

  const buildFiltersWrapper = React.useCallback(
    (
      filtersList: Array<TableFilter & { fieldData?: TableField }>,
      valuesOverride?: Record<string, FilterValueState>
    ): RecordsFilter[] => {
      const activeValues = valuesOverride ?? filterValues;
      return buildFiltersBody(filtersList, activeValues);
    },
    [filterValues]
  );

  React.useEffect(() => {
    setSavedReport(false);
    setSaveFeedback(null);
    setSaveError(null);
  }, [filterValues]);

  const filtersDisabled = !appliedTableId || switchAccountMutation.isPending;
  const isSearchLoading = searchRequested && recordsFetching;
  const exportDisabled = filtersDisabled || fieldsLoading || tablesLoading;
  const accountMaxReport = typeof account?.maxReport === 'number' ? account.maxReport : undefined;
  const totalItems = recordsData?.total ?? tableRecords.length;
  const withinReportLimit =
    typeof accountMaxReport === 'number' ? totalItems <= accountMaxReport : true;
  const canSaveReport = !filtersDisabled && !fieldsLoading && !tablesLoading && withinReportLimit;

  const setExportError = React.useCallback(
    (message: string) => {
      setSearchError(message);
      if (!hasSearchRun) setHasSearchRun(true);
    },
    [hasSearchRun, setHasSearchRun, setSearchError]
  );

  const clearExportTimer = React.useCallback(() => {
    if (exportTimerRef.current !== null) {
      window.clearInterval(exportTimerRef.current);
      exportTimerRef.current = null;
    }
  }, []);

  const getVisibleColumnNames = React.useCallback(() => {
    if (!allColumnNames.length) return [];
    return allColumnNames.filter(col => activeColumnVisibility[col] !== false);
  }, [activeColumnVisibility, allColumnNames]);

  const buildReportColumns = React.useCallback(() => {
    const visibleColumnNames = getVisibleColumnNames();
    const hasExplicitVisibility = Object.keys(activeColumnVisibility).length > 0;
    if (hasExplicitVisibility && !visibleColumnNames.length) {
      return [];
    }

    const visibleSet = new Set(visibleColumnNames);
    const selectedMappingIds = mapping
      .filter(column => {
        const name = getMappingColumnName(column);
        return Boolean(name) && visibleSet.has(name);
      })
      .map(column => column._id)
      .filter((id): id is string => Boolean(id));

    if (selectedMappingIds.length) {
      return selectedMappingIds.map(id => ({ field: id }));
    }

    if (!mapping.length) {
      const fieldIds = fields.map(field => field.id);
      return fieldIds.map(id => ({ field: id }));
    }

    return [];
  }, [activeColumnVisibility, fields, getVisibleColumnNames, mapping]);

  const buildReportPayload = React.useCallback(
    (reportName: string) => {
      if (!activeTableId) {
        setExportError('Selecciona una tabla para guardar el reporte.');
        return null;
      }

      if (!reportName.trim()) {
        setExportError('Para guardar reporte, debes agregar un nombre.');
        return null;
      }

      const columnsPayload = buildReportColumns();
      if (!columnsPayload.length) {
        setExportError('Para guardar reporte, debes seleccionar al menos una columna.');
        return null;
      }

      return {
        name: reportName.trim(),
        filters: buildFiltersWrapper(mappedFilters),
        columns: columnsPayload,
        analysis: [],
        detailed: detailActived,
        demoMode,
      };
    },
    [
      activeTableId,
      buildReportColumns,
      buildFiltersWrapper,
      mappedFilters,
      demoMode,
      detailActived,
      setExportError,
    ]
  );

  const resolveReportFileUrl = React.useCallback((file: string) => {
    if (!file) return '';
    if (file.startsWith('http://') || file.startsWith('https://')) return file;
    if (file.startsWith('//')) return `${window.location.protocol}${file}`;
    if (file.startsWith('/')) return `${window.location.origin}${file}`;
    return `${window.location.protocol}//${file}`;
  }, []);

  const handleExportFile = React.useCallback(() => {
    const fileUrl = exportFileRef.current;
    if (!fileUrl) return;
    window.location.href = fileUrl;
    exportFileRef.current = null;
  }, []);

  const checkExportStatus = async () => {
    try {
      const response = await fetchReportStatus();
      const status = response?.status;
      const resolvedFilePath = extractReportFilePath(response);
      if (resolvedFilePath) {
        exportFileRef.current = resolveReportFileUrl(resolvedFilePath);
      }

      if (status === 'processing') return;

      if (status === 'error') {
        setExportError(
          'No fue posible generar el reporte. Valida los filtros e intenta nuevamente.'
        );
        clearExportTimer();
        setIsExportingReport(false);
        return;
      }

      if (status === 'success') {
        clearExportTimer();
        setIsExportingReport(false);
        if (!exportFileRef.current) {
          setExportError('El reporte se generó, pero no se recibió la URL del archivo.');
          return;
        }
        handleExportFile();
        return;
      }

      if (typeof status === 'string') {
        setExportError(status);
      }
    } catch {
      clearExportTimer();
      setIsExportingReport(false);
      setExportError('No se pudo completar la exportación.');
    }
  };

  const handleSearch = () => {
    const dateError = validateDateRange(mappedFilters, filterValues);
    if (dateError) {
      setSearchError(dateError);
      setHasSearchRun(true);
      return;
    }
    if (!visibleTableColumns.length) {
      setSearchError('Selecciona al menos una columna para buscar.');
      setHasSearchRun(true);
      return;
    }
    const filters = buildFiltersWrapper(mappedFilters);
    setSearchError(null);
    setSearchRequested(true);
    setHasSearchRun(true);
    setAppliedFilters(filters);
  };

  const _handleExportReport = async () => {
    if (isExportingReport) return;
    const dateError = validateDateRange(mappedFilters, filterValues);
    if (dateError) {
      setExportError(dateError);
      return;
    }

    const reportName = `Reporte temporal para Excel ${formatReportTimestamp(new Date())}`;
    const payload = buildReportPayload(reportName);
    if (!payload) return;

    try {
      setIsExportingReport(true);
      setSearchError(null);
      const response = await requestLegacyExport({ ...payload, generate: true });
      const file = extractReportFilePath(response);

      if (file) {
        exportFileRef.current = resolveReportFileUrl(file);
      }
      clearExportTimer();
      exportTimerRef.current = window.setInterval(() => {
        void checkExportStatus();
      }, 3000);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      const canRecoverWithPolling =
        isReportGenerationInProgressError(error) ||
        (typeof message === 'string' &&
          /load failed|failed to fetch|network/i.test(message.toLowerCase()));

      if (canRecoverWithPolling) {
        setExportError('Hay una exportación en curso. Validando estado...');
        clearExportTimer();
        exportTimerRef.current = window.setInterval(() => {
          void checkExportStatus();
        }, 3000);
        return;
      }

      setExportError(message || 'No se pudo generar el reporte.');
      setIsExportingReport(false);
    }
  };

  const handleExportBackend = React.useCallback(async () => {
    if (isExportingReport) return;

    if (!activeTableId) {
      setDownloadError('Selecciona una tabla para descargar el reporte.');
      return;
    }

    if (!appliedFilters.length) {
      setDownloadError('Aplica al menos un filtro para exportar el reporte.');
      return;
    }

    try {
      setIsExportingReport(true);
      setDownloadError(null);
      const visibleColumns = getVisibleColumnNames();
      await exportTableRecords(activeTableId, appliedFilters, visibleColumns);
      const currentTable = tables.find(t => t._id === activeTableId);
      trackExportEvent({
        tableId: activeTableId,
        tableName: currentTable?.name ?? '',
        rowCount: recordsData?.total ?? 0,
        filters: appliedFilters,
      }).catch(() => undefined);
    } catch (error) {
      const message = extractApiErrorMessage(error);
      setDownloadError(message || 'No se pudo exportar el archivo.');
    } finally {
      setIsExportingReport(false);
    }
  }, [
    activeTableId,
    appliedFilters,
    getVisibleColumnNames,
    isExportingReport,
    tables,
    recordsData?.total,
  ]);

  const handleSaveReport = React.useCallback(
    async (reportName: string) => {
      if (isSavingReport) return false;

      const payload = buildReportPayload(reportName);
      if (!payload) {
        setSaveError(searchError || 'Completa el nombre y asegúrate de tener columnas visibles.');
        return false;
      }

      setSearchError(null);
      setSaveError(null);
      setSaveFeedback(null);

      try {
        setIsSavingReport(true);
        const createdReport = await createSavedReport(activeTableId as string, payload);
        setSavedReport(true);
        setSaveFeedback('Búsqueda guardada y lista para reutilizar.');
        if (activeTableId) {
          queryClient.setQueryData<PaginatedResponse<SavedReportItem> | undefined>(
            queryKeys.savedReports(activeTableId),
            current => {
              const items = current?.data ?? [];
              const normalizedReport: SavedReportItem = {
                ...createdReport,
                tableId: createdReport.tableId ?? activeTableId,
                name: createdReport.name ?? payload.name,
                filters: createdReport.filters ?? payload.filters,
                columns: createdReport.columns ?? payload.columns,
                analysis: createdReport.analysis ?? payload.analysis ?? [],
                detailed: createdReport.detailed ?? payload.detailed ?? false,
                demoMode: createdReport.demoMode ?? payload.demoMode ?? false,
              };

              const deduped = items.filter(item => item.id !== normalizedReport.id);
              return {
                ...(current ?? { data: [], total: 0, page: 1, limit: 1000, totalPages: 0 }),
                data: [normalizedReport, ...deduped],
              };
            }
          );
          await queryClient.invalidateQueries({ queryKey: queryKeys.savedReports(activeTableId) });
        }
        return true;
      } catch (error) {
        const message = extractApiErrorMessage(error) || 'No se pudo guardar el reporte.';
        setSavedReport(false);
        setSaveError(message);
        return false;
      } finally {
        setIsSavingReport(false);
      }
    },
    [activeTableId, buildReportPayload, isSavingReport, queryClient, searchError]
  );

  const handleOpenSavedReport = React.useCallback(
    async (report: SavedReportItem) => {
      try {
        setOpeningSavedReportId(report.id);
        setSearchError(null);
        await openSavedSearch(report);
      } catch (error) {
        const message = extractApiErrorMessage(error) || 'No se pudo abrir la búsqueda guardada.';
        setSearchError(message);
      } finally {
        setOpeningSavedReportId(null);
      }
    },
    [openSavedSearch]
  );

  const handleDeleteSavedReport = React.useCallback(
    async (report: SavedReportItem) => {
      const confirmed = window.confirm(
        `¿Eliminar la búsqueda guardada "${report.name ?? 'Sin nombre'}"?`
      );
      if (!confirmed) return;

      try {
        setDeletingSavedReportId(report.id);
        setSearchError(null);
        await deleteSavedReportMutation.mutateAsync(report.id);
      } catch (error) {
        const message =
          extractApiErrorMessage(error) || 'No se pudo eliminar la búsqueda guardada.';
        setSearchError(message);
      } finally {
        setDeletingSavedReportId(null);
      }
    },
    [deleteSavedReportMutation]
  );

  React.useEffect(() => {
    if (!searchRequested) return;
    if (recordsLoading || recordsFetching) return;
    const items = recordsData?.data ?? [];
    if (recordsError instanceof ApiError && recordsError.status === 400) {
      setSearchError(
        'Tu búsqueda retorna demasiados resultados para mostrar. Refina los filtros para reducir el volumen de datos y obtener resultados más precisos.'
      );
    } else if (recordsError && extractApiErrorMessage(recordsError)) {
      // El API explica el motivo —«No tienes acceso a las partidas 8703…,
      // contacta a tu asesor para ampliar tu plan»— y antes se perdía: todo
      // error que no fuera un 400 acababa en el genérico «no se encontró
      // información», que hace pensar que no hay datos en vez de que no están
      // contratados. Se usa el mismo extractor que el resto del archivo.
      setSearchError(extractApiErrorMessage(recordsError));
    } else if (!items.length) {
      setSearchError('No se encontró información que coincidiera con los criterios de búsqueda.');
    }
    setSearchRequested(false);
  }, [searchRequested, recordsLoading, recordsFetching, recordsData, recordsError]);

  React.useEffect(() => {
    if (!pendingHistoricSearch || historicApplied) return;
    if (!mappedFilters.length) return;
    setFilterValues(prev => {
      const pendingFilters = pendingHistoricSearch.filters;
      const nextValues = Array.isArray(pendingFilters)
        ? hydrateFiltersFromSavedReport(mappedFilters, pendingFilters as RecordsFilter[], prev)
        : hydrateFiltersFromHistoric(mappedFilters, pendingHistoricSearch, prev);
      const filters = buildFiltersWrapper(mappedFilters, nextValues);
      setAppliedFilters(filters);
      setHistoricApplied(true);
      setHasSearchRun(true);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(HISTORIC_STORAGE_KEY);
      }
      return nextValues;
    });
  }, [pendingHistoricSearch, historicApplied, mappedFilters, buildFiltersWrapper]);

  React.useEffect(() => {
    if (!pendingSavedSearch || savedSearchApplied) return;
    if (!mappedFilters.length) return;
    if (pendingSavedSearch.tableId !== activeTableId) return;

    setFilterValues(prev => {
      const nextValues = hydrateFiltersFromSavedReport(
        mappedFilters,
        pendingSavedSearch.filters,
        prev
      );
      const filters = buildFiltersWrapper(mappedFilters, nextValues);
      setAppliedFilters(filters);
      setDetailActived(pendingSavedSearch.detailed);
      setDemoMode(pendingSavedSearch.demoMode);
      setActiveColumnVisibility(
        fieldIdsToVisibilityState(
          allColumnNames,
          mapping,
          fields,
          pendingSavedSearch.columnFieldIds
        )
      );
      setSavedSearchApplied(true);
      setHasSearchRun(true);
      setSavedReport(true);
      setSaveFeedback(`Búsqueda “${pendingSavedSearch.reportName}” cargada.`);
      setPendingSavedSearch(null);
      clearPendingSavedSearchPayload();
      return nextValues;
    });
  }, [
    activeTableId,
    allColumnNames,
    buildFiltersWrapper,
    datasetId,
    fields,
    mapping,
    mappedFilters,
    pendingSavedSearch,
    savedSearchApplied,
    setDemoMode,
    setDetailActived,
  ]);

  const renderFilterField = React.useCallback(
    (filter: TableFilter & { fieldData?: TableField }) => {
      const filterKey = filter.id;
      return (
        <div key={filterKey}>
          <FilterFieldRenderer
            filter={filter}
            state={filterValues[filter.id]}
            inputClassName={inputClassName}
            filtersDisabled={filtersDisabled}
            updateFilterState={updateFilterState}
            fetchFilterOptions={fetchFilterOptions}
            yearQuickSelects={yearQuickSelects}
            tableDateRange={tableDateRange}
            onYearQuickSelect={handleYearQuickSelect}
          />
        </div>
      );
    },
    [
      fetchFilterOptions,
      filterValues,
      filtersDisabled,
      handleYearQuickSelect,
      tableDateRange,
      updateFilterState,
      yearQuickSelects,
    ]
  );

  return (
    <>
      <style>{`
        body.hide-header-desktop header {
          display: block;
        }
        @media (min-width: 1024px) {
          body.hide-header-desktop header {
            display: none;
          }
        }
      `}</style>
      <div className="h-screen overflow-hidden bg-background-light font-body">
        {/* Mobile backdrop */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        <div className="flex flex-col h-full lg:flex-row">
          <aside
            className={`fixed inset-y-0 left-0 z-50 h-screen w-72 border transition-transform duration-300 lg:relative lg:z-30 lg:border-b-0 lg:border-r lg:transition-none ${sidebarClasses.container} ${
              mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0 ${sidebarCollapsed ? 'lg:w-24' : 'lg:w-72'}`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between gap-3 px-4 py-5 lg:px-5">
                <DaaterLogoHomeLink
                  alt="Daater logo"
                  className="inline-flex"
                  imgClassName={`${sidebarClasses.logo} ${
                    sidebarCollapsed ? 'h-10 w-10 object-contain' : 'h-9 w-auto object-contain'
                  }`}
                />
                <div className="flex items-center gap-2">
                  {/* Mobile close button */}
                  <button
                    type="button"
                    onClick={() => setMobileMenuOpen(false)}
                    className={`inline-flex items-center justify-center rounded-full border p-1.5 transition lg:hidden ${sidebarClasses.iconButton}`}
                    aria-label="Cerrar menú"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  {/* Desktop collapse button */}
                  <button
                    type="button"
                    onClick={() => setSidebarCollapsed(prev => !prev)}
                    className={`hidden items-center justify-center rounded-full border p-1.5 transition lg:inline-flex ${sidebarClasses.iconButton}`}
                    aria-label={sidebarCollapsed ? 'Expandir sidebar' : 'Contraer sidebar'}
                  >
                    {sidebarCollapsed ? (
                      <ChevronRight className="w-4 h-4" />
                    ) : (
                      <ChevronLeft className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex flex-col flex-1 gap-8 px-4 pt-4 pb-6 overflow-y-auto lg:px-5">
                <div className={sidebarCollapsed ? 'hidden' : ''}>
                  <div className="mb-4">
                    <p
                      className={`mb-3 text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.sectionLabel}`}
                    >
                      País
                    </p>
                    <div className="relative">
                      <span className="absolute inset-y-0 flex items-center pointer-events-none left-2">
                        <span
                          className={`relative flex h-5 w-5 items-center justify-center rounded-md ${sidebarClasses.selectFlag}`}
                        >
                          <span className="relative z-10 text-base">
                            {getCountryFlag(currentSectionCode)}
                          </span>
                        </span>
                      </span>
                      <select
                        className={`w-full appearance-none rounded-md border py-2 pl-7 pr-6 text-sm ${sidebarClasses.select} [&>option]:bg-white [&>option]:text-gray-900`}
                        value={selectedSection ?? ''}
                        disabled={dataStructureLoading}
                        onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                          const newSection = event.target.value;
                          setSelectedSection(newSection);
                          setMobileMenuOpen(false);
                          const sectionDatasets = sectionData.bySection.get(newSection) ?? [];
                          const firstDataset = sectionDatasets[0];
                          if (firstDataset) {
                            setSelectedDataset(firstDataset._id);
                            const firstTable = (dataStructure?.tables ?? []).filter(
                              t => t.datasetId === firstDataset._id
                            )[0];
                            if (firstTable) {
                              void handleTableApply(firstTable._id, firstDataset._id);
                            } else {
                              setAppliedTableId(undefined);
                            }
                          } else {
                            setSelectedDataset(undefined);
                            setAppliedTableId(undefined);
                          }
                        }}
                      >
                        {sectionData.list.map(section => (
                          <option key={section} value={section}>
                            {section}
                          </option>
                        ))}
                      </select>
                      <span
                        className={`absolute inset-y-0 flex items-center pointer-events-none right-2 ${sidebarClasses.selectCaret}`}
                      >
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 20 20"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        >
                          <path d="M6 8l4 4 4-4" strokeLinecap="round" />
                        </svg>
                      </span>
                    </div>
                  </div>
                  <div className={`pb-2 mb-4 border-b ${sidebarClasses.baseDivider}`}>
                    <p
                      className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.sectionLabel}`}
                    >
                      Base
                    </p>
                  </div>
                  <div
                    className="flex flex-col gap-2"
                    role="tablist"
                    aria-label="Bases disponibles"
                  >
                    {datasetsForSection.length ? (
                      datasetsForSection.map(dataset => {
                        const isActive = !activeTableIsFromSidebar && dataset._id === datasetId;
                        return (
                          <button
                            key={dataset._id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={dataStructureLoading}
                            onClick={() => {
                              setSelectedSection(dataset.section);
                              setSelectedDataset(dataset._id);
                              setMobileMenuOpen(false);
                              const datasetTables = (dataStructure?.tables ?? []).filter(
                                t => t.datasetId === dataset._id
                              );
                              if (datasetTables.length >= 1) {
                                void handleTableApply(datasetTables[0]._id, dataset._id);
                              }
                            }}
                            className={`${sidebarTabClassName} ${
                              isActive ? sidebarClasses.baseActive : sidebarClasses.baseInactive
                            }`}
                          >
                            <ChartBarBigIcon className={sidebarClasses.baseIcon} />
                            {dataset.name}
                          </button>
                        );
                      })
                    ) : (
                      <span className={`text-sm ${sidebarClasses.emptyText}`}>
                        {dataStructureLoading ? 'Cargando...' : 'Sin bases disponibles.'}
                      </span>
                    )}
                  </div>
                </div>

                {!sidebarCollapsed && sidebarTables.length > 0 && (
                  <div>
                    <div className={`pb-2 mb-4 border-b ${sidebarClasses.baseDivider}`}>
                      <p
                        className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.sectionLabel}`}
                      >
                        Tablas
                      </p>
                    </div>
                    <div
                      className="flex flex-col gap-2"
                      role="tablist"
                      aria-label="Tablas disponibles"
                    >
                      {sidebarTables.map(table => {
                        const isActive = table._id === appliedTableId;
                        return (
                          <button
                            key={table._id}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            disabled={dataStructureLoading || switchAccountMutation.isPending}
                            onClick={() => {
                              if (table.datasetId) setSelectedDataset(table.datasetId);
                              void handleTableApply(table._id, table.datasetId ?? undefined);
                              setMobileMenuOpen(false);
                            }}
                            className={`${sidebarTabClassName} ${
                              isActive ? sidebarClasses.baseActive : sidebarClasses.baseInactive
                            }`}
                          >
                            <FileSpreadsheet className={sidebarClasses.baseIcon} />
                            {table.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {sidebarCollapsed ? (
                  <div className="hidden lg:flex lg:flex-col lg:gap-6 lg:pt-2">
                    <div className="flex flex-col items-center gap-2">
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-widest ${sidebarClasses.collapsedLabel}`}
                      >
                        Base
                      </span>
                      <div className="flex flex-col items-center gap-2">
                        {datasetsForSection.length ? (
                          datasetsForSection.map(dataset => {
                            const isActive = !activeTableIsFromSidebar && dataset._id === datasetId;
                            const label = dataset.name ?? 'Base';
                            return (
                              <button
                                key={dataset._id}
                                type="button"
                                title={label}
                                aria-label={label}
                                disabled={dataStructureLoading}
                                onClick={() => {
                                  setSelectedSection(dataset.section);
                                  setSelectedDataset(dataset._id);
                                  setMobileMenuOpen(false);
                                  const datasetTables = (dataStructure?.tables ?? []).filter(
                                    t => t.datasetId === dataset._id
                                  );
                                  if (datasetTables.length >= 1) {
                                    void handleTableApply(datasetTables[0]._id, dataset._id);
                                  }
                                }}
                                className={`flex h-9 w-9 items-center justify-center rounded-full border shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)] ${
                                  isActive
                                    ? sidebarClasses.collapsedBaseActive
                                    : sidebarClasses.collapsedBaseInactive
                                }`}
                              >
                                <FileSpreadsheet className="w-4 h-4" />
                              </button>
                            );
                          })
                        ) : (
                          <span className={`text-xs ${sidebarClasses.emptyText}`}>-</span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                <div className={`flex-1 ${sidebarCollapsed ? 'hidden lg:block' : ''}`} />
              </div>

              <div
                className={`mt-auto px-4 pb-6 lg:px-5 ${
                  sidebarCollapsed ? 'lg:flex lg:justify-center' : ''
                }`}
              >
                <div className="relative" ref={menuRef}>
                  <button
                    type="button"
                    onClick={() => setMenuOpen(prev => !prev)}
                    className={`flex w-full items-center gap-3 rounded-full border px-3 py-2 text-sm transition ${sidebarClasses.menuButton} ${
                      sidebarCollapsed ? 'justify-center px-2' : ''
                    }`}
                    aria-haspopup="true"
                    aria-expanded={menuOpen}
                  >
                    <div className="flex items-center justify-center text-xs font-semibold bg-white/90 rounded-full h-9 w-9 shrink-0 text-primary aspect-square shadow-[0_10px_24px_-16px_rgba(0,0,0,0.7)]">
                      {initials}
                    </div>
                    {sidebarCollapsed ? null : (
                      <div className="flex items-center flex-1 min-w-0 gap-2">
                        <div className="min-w-0">
                          <span
                            className={`block text-sm font-medium truncate ${
                              isWhiteBlueSidebar ? 'text-slate-900' : 'text-white'
                            }`}
                          >
                            {displayName}
                          </span>
                          <span
                            className={`block truncate text-[11px] ${sidebarClasses.menuSubText}`}
                          >
                            {accountEmail}
                          </span>
                        </div>
                        <ChevronDown className={`ml-auto h-4 w-4 ${sidebarClasses.menuChevron}`} />
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </aside>

          <div className="flex-1 overflow-y-auto">
            {/* Sticky navigation bar with breadcrumb and tabs */}
            <div className="sticky z-20 top-2">
              <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                <div className="flex flex-col gap-3 rounded-xl bg-white px-4 py-3 shadow-[0_4px_20px_-4px_rgba(26,46,88,0.25)] sm:flex-row sm:items-center sm:justify-between sm:px-6">
                  {/* Breadcrumb */}
                  <nav aria-label="Breadcrumb" className="flex-shrink-0 min-w-0">
                    <ol className="flex items-center gap-2 text-xs">
                      <li className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs rounded-full bg-primary/10">
                          {getCountryFlag(currentSectionCode)}
                        </span>
                        <span className="font-medium text-gray-700">
                          {currentSection ?? 'País'}
                        </span>
                      </li>
                      {activeTableIsFromSidebar ? (
                        <>
                          <li className="text-gray-400" aria-hidden="true">
                            ›
                          </li>
                          <li className="truncate max-w-[200px] text-gray-600">
                            {sidebarTables.find(t => t._id === appliedTableId)?.name}
                          </li>
                        </>
                      ) : currentDataset ? (
                        <>
                          <li className="text-gray-400" aria-hidden="true">
                            ›
                          </li>
                          <li className="truncate max-w-[200px] text-gray-600">
                            {currentDataset.name}
                          </li>
                        </>
                      ) : null}
                    </ol>
                  </nav>

                  {/* Tabs + bell */}
                  <div className="flex items-center gap-3">
                    {!activeTableIsFromSidebar && (
                      <div
                        className={`flex flex-wrap items-center gap-1.5 rounded-lg border p-1 ${
                          detailActived
                            ? 'border-orange-200/80 bg-orange-50/70'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                        role="tablist"
                        aria-label="Tablas disponibles"
                      >
                        {tables.length ? (
                          tables.map(table => {
                            const isActive = table._id === appliedTableId;
                            return (
                              <button
                                key={table._id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                disabled={tablesLoading || switchAccountMutation.isPending}
                                onClick={() => void handleTableApply(table._id)}
                                className={`rounded-md px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-all whitespace-nowrap ${
                                  isActive
                                    ? detailActived
                                      ? 'bg-orange-500 text-white shadow-sm'
                                      : 'bg-primary text-white shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                } disabled:cursor-not-allowed disabled:opacity-50`}
                              >
                                {table.name ?? table._id}
                              </button>
                            );
                          })
                        ) : (
                          <span className="px-3 py-1.5 text-xs text-gray-500">
                            {tablesLoading ? 'Cargando...' : 'Sin tablas'}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="relative shrink-0" ref={notifRef}>
                      <button
                        type="button"
                        aria-label="Notificaciones"
                        onClick={() => setNotifOpen(prev => !prev)}
                        className="relative flex items-center justify-center w-8 h-8 rounded-lg text-slate hover:bg-pale-grey transition"
                      >
                        <Bell className="w-4 h-4" />
                        {unreadCount > 0 && (
                          <span className="absolute top-0.5 right-0.5 flex items-center justify-center w-3.5 h-3.5 text-[8px] font-bold text-white bg-red-500 rounded-full">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </button>
                      {notifOpen && <NotificationDropdown onClose={() => setNotifOpen(false)} />}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <section className={`pt-8 pb-28 `}>
              {/*<section className={`pt-8 pb-28 `}>*/}
              <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
                {/* Mobile hamburger button */}
                <button
                  type="button"
                  onClick={() => setMobileMenuOpen(true)}
                  className="mb-6 inline-flex items-center justify-center rounded-lg bg-white/10 p-2.5 text-white backdrop-blur-sm transition hover:bg-white/20 lg:hidden"
                  aria-label="Abrir menú"
                >
                  <Menu className="w-5 h-5" />
                </button>

                <div className="p-6 bg-white border border-transparent shadow-xl rounded-xl md:p-8">
                  {filtersLoading || fieldsLoading ? (
                    <p className="text-sm text-slate">Cargando filtros...</p>
                  ) : filtersToShow.length ? (
                    <div className={`space-y-6 ${filtersDisabled ? 'opacity-60' : ''}`}>
                      <div className="grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
                        {filtersToShow.map(filter => renderFilterField(filter))}
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-4 pt-6 border-t border-gray-100">
                        {/* Detail mode toggle - only shown if user has access */}
                        <div className="flex items-center">
                          {canUseDetailMode && (
                            <DetailModeSwitch
                              checked={detailActived}
                              onChange={handleToggleDetailMode}
                              disabled={filtersDisabled}
                            />
                          )}
                        </div>

                        {/* Filter action buttons */}
                        <div className="flex items-center gap-6">
                          <button
                            className="text-sm font-medium text-slate hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                            type="button"
                            disabled={filtersDisabled}
                            onClick={handleClearFilters}
                          >
                            Limpiar
                          </button>
                          <button
                            className="text-sm font-semibold text-primary hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                            type="button"
                            onClick={() => setMoreFiltersActive(prev => !prev)}
                            disabled={filtersDisabled}
                          >
                            {moreFiltersActive ? 'Menos filtros' : 'Más filtros'}
                          </button>
                          <button
                            className={`flex h-12 w-12 items-center justify-center text-white transition rounded-full shadow-lg hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60 ${
                              detailActived ? 'bg-orange-500 hover:bg-orange-600' : 'bg-primary'
                            }`}
                            type="button"
                            onClick={handleSearch}
                            disabled={filtersDisabled || isSearchLoading}
                          >
                            {isSearchLoading ? (
                              <span className="inline-flex h-5 w-5 border-2 rounded-full animate-spin border-white/30 border-t-white" />
                            ) : (
                              <svg
                                className="h-5 w-5"
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                              >
                                <circle cx="9" cy="9" r="5.5" />
                                <path d="M13.5 13.5L18 18" strokeLinecap="round" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-slate">
                      {appliedTableId
                        ? 'No hay filtros configurados para esta tabla.'
                        : 'Selecciona el país, la base y si buscas exportaciones o importaciones para habilitar los filtros.'}
                    </p>
                  )}
                </div>
              </div>
            </section>

            <div className="pb-20 -mt-16">
              <div className="w-full mx-auto max-w-7xl sm:px-6 lg:px-8">
                {!hasSearchRun ? (
                  <div className="relative mb-8 -mt-4 px-4 sm:px-0">
                    <SearchCards currentTableId={appliedTableId} />
                  </div>
                ) : null}
                {isDemoResponse && hasSearchRun ? (
                  <div className="px-4 py-4 mx-4 mb-6 text-sm border rounded-lg bg-amber-50 border-amber-200 text-amber-800 sm:mx-0">
                    <p className="mb-2 font-bold">Bienvenido a la DEMO de DAATER Detalle.</p>
                    <p className="mb-2">
                      La primera plataforma que incluye: Modelo, Marca, Referencia, Principio
                      activo, Concentración y los detalles que llevabas años buscando.
                    </p>
                    <p className="mb-2">
                      Mostrando los 5 primeros resultados. Para ver todos los resultados contacta a
                      su asesor y comience a ver los detalles de las importaciones de todo su
                      mercado y competidores en solo 5 minutos.
                    </p>
                    <p className="font-medium">
                      Tel + 57 2 488 22 54, Cel + 57 301 635 90 17 Email: soporte@daater.com
                    </p>
                  </div>
                ) : null}
                {deniedPartidas.length > 0 ? (
                  <div className="px-4 py-4 mx-4 mb-6 text-sm border rounded-lg bg-amber-50 border-amber-200 text-amber-800 sm:mx-0">
                    <p className="mb-2 font-bold">
                      Algunas partidas de tu búsqueda no están en tu plan de detalle.
                    </p>
                    <p className="mb-2">
                      No se incluyeron los resultados de: {deniedPartidas.join(', ')}. Lo que ves
                      corresponde solo a las partidas que sí tienes contratadas.
                    </p>
                    <p className="font-medium">
                      Contacta a tu asesor para ampliar tu plan y ver también esas partidas.
                    </p>
                  </div>
                ) : null}
                {hasSearchRun && searchError ? (
                  <div className="px-4 py-3 mx-4 mb-6 text-sm bg-white border rounded-lg border-darkish-pink/30 text-darkish-pink sm:mx-0">
                    {searchError}
                  </div>
                ) : null}
                {hasSearchRun ? (
                  <div className="space-y-8">
                    <AnalysisSection
                      records={displayRecords}
                      mapping={[]}
                      fields={fields}
                      filters={filters}
                      loading={recordsLoading || recordsFetching}
                      storageKey={activeTableId}
                      tableId={activeTableId}
                      appliedFilters={appliedFilters}
                    />
                    <DataSection
                      data={displayRecords}
                      columns={columns}
                      loading={recordsLoading || recordsFetching}
                      totalItems={recordsData?.total}
                      onDownload={() => void handleExportBackend()}
                      isDownloading={isExportingReport}
                      downloadDisabled={exportDisabled || isExportingReport || demoMode}
                      downloadError={downloadError}
                      onSave={handleSaveReport}
                      saveError={saveError}
                      saveSuccessMessage={saveFeedback}
                      isSaving={isSavingReport}
                      saveDisabled={!canSaveReport || demoMode}
                      saved={savedReport}
                      initialColumnVisibility={initialColumnVisibility}
                      onColumnVisibilityChange={handleColumnVisibilityChange}
                    />
                    {recordsData && recordsData.total > 1000 && (
                      <p className="text-sm text-muted-foreground">
                        *Mostrando 1000 resultados, para ver la información completa por favor
                        descarga el archivo en Excel.
                      </p>
                    )}
                    <SearchCards currentTableId={appliedTableId} />
                  </div>
                ) : null}
                {appliedTableId ? (
                  <div className={hasSearchRun ? 'mt-8' : ''}>
                    <SavedSearchesSection
                      reports={savedReports}
                      datasets={datasets}
                      tables={dataStructure?.tables ?? []}
                      currentTableId={appliedTableId}
                      loading={savedReportsLoading}
                      openingReportId={openingSavedReportId}
                      deletingReportId={deletingSavedReportId}
                      onOpen={handleOpenSavedReport}
                      onDelete={handleDeleteSavedReport}
                      compact
                      emptyMessage="Aún no has guardado búsquedas para esta base."
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {menuOpen ? (
        <div
          ref={popupRef}
          className="fixed z-[60] w-80 max-w-[calc(100vw-1.5rem)] rounded-xl border border-pale-grey-three bg-white p-4 text-slate shadow-xl"
          style={{ bottom: popupPosition.bottom, left: popupPosition.left }}
          role="menu"
        >
          <div className="pb-4 mb-4 border-b border-pale-grey-three">
            <p className="text-sm font-semibold text-charcoal-grey">{displayName}</p>
            <div className="mt-3 overflow-hidden border rounded-lg border-pale-grey-three">
              <div className="grid grid-cols-3 text-xs">
                <div className="col-span-1 border-b border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                  Usuario
                </div>
                <div className="col-span-2 border-b border-pale-grey-three p-1.5 text-charcoal-grey">
                  {accountEmail}
                </div>
                <div className="col-span-1 border-b border-pale-grey-three bg-pale-grey p-1.5 font-semibold text-slate">
                  Plan
                </div>
                <div className="col-span-2 border-b border-pale-grey-three p-1.5 text-charcoal-grey">
                  {accountPlan &&
                  typeof accountPlan === 'object' &&
                  'name' in accountPlan &&
                  (typeof (accountPlan as Record<string, unknown>).name === 'string' ||
                    typeof (accountPlan as Record<string, unknown>).name === 'number')
                    ? String((accountPlan as Record<string, unknown>).name)
                    : '-'}
                </div>
                <div className="col-span-1 bg-pale-grey p-1.5 font-semibold text-slate">
                  Días restantes
                </div>
                <div className="col-span-1 p-1.5 text-charcoal-grey">
                  {typeof accountDaysLeft === 'number' ? accountDaysLeft : '-'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <a
                className="flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-primary hover:border-primary/40 hover:bg-primary/5"
                href="https://daater.com/precios-y-planes/"
                target="_blank"
                rel="noreferrer"
              >
                <Package className="w-4 h-4" />
                Planes
              </a>
              <button
                type="button"
                className="flex items-center justify-center w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-pale-grey-three text-slate hover:border-primary/40 hover:bg-primary/5"
                onClick={() => {
                  setMenuOpen(false);
                  void navigate({ to: '/payment-history' });
                }}
              >
                <CreditCard className="w-4 h-4" />
                Pagos
              </button>
            </div>
            <button
              type="button"
              className="flex items-center justify-start w-full gap-2 px-3 py-2 text-sm font-medium transition border rounded-lg border-darkish-pink/30 text-darkish-pink hover:bg-darkish-pink/5"
              onClick={() => {
                setMenuOpen(false);
                void logoutRequest().then(() => {
                  clearAuth();
                  clearAccount();
                  queryClient.clear();
                  clearDashboardSession();
                  void navigate({ to: '/login' });
                });
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar sesion
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
